import { translations } from '../data/translations';

/** Langues du site (suit translations.js — nouvelle langue = nouvel onglet admin). */
export const SITE_LANGUAGE_CODES = Object.freeze(Object.keys(translations));

export const SITE_LANGUAGE_LABELS = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
};

export const SITE_LANGUAGE_FLAGS = {
  en: '🇬🇧',
  es: '🇪🇸',
  fr: '🇫🇷',
};

const INVALID_TRANSLATION_MARKERS = [
  'rien à traduire',
  'déjà en anglais',
  'already in english',
  'je dois clarifier',
  'fournis-moi le texte',
  'quel est ton besoin',
];

export function getSiteLanguageLabel(code) {
  return SITE_LANGUAGE_LABELS[code] || String(code || '').toUpperCase();
}

export function emptyMessageTranslations() {
  return SITE_LANGUAGE_CODES.reduce((acc, lang) => {
    acc[lang] = '';
    return acc;
  }, {});
}

/** Réponse meta de l'IA (refus / explication) — ne pas afficher sur le site. */
export function isBrokenTranslation(text) {
  if (!text || !String(text).trim()) return true;
  const s = String(text).toLowerCase();
  if (s.length > 280) return true;
  if (INVALID_TRANSLATION_MARKERS.some((m) => s.includes(m))) return true;
  return false;
}

/** Texte manifestement dans la mauvaise langue pour cet onglet. */
export function isWrongLanguageForCode(text, langCode) {
  if (!text || isBrokenTranslation(text)) return true;
  const s = String(text).toLowerCase();
  if (langCode === 'es') {
    if (/\b(offre|utilise|utilisez|réduction|réservation|votre|limitée)\b/i.test(s)) return true;
    if (/[éèêë]/.test(s) && !/\b(código|descuento|oferta|reserva|utiliza)\b/i.test(s)) return true;
  }
  if (langCode === 'fr' && /\b(oferta|descuento|código)\b/i.test(s)) return true;
  if (langCode === 'en' && /\b(offre|oferta|réduction|descuento)\b/i.test(s)) return true;
  return false;
}

/**
 * Détection heuristique sur UN seul texte (jamais sur plusieurs langues mélangées).
 */
export function detectSourceLanguage(text) {
  if (!text || !String(text).trim()) return null;
  const lowerText = String(text).toLowerCase();

  const frenchMarkers = /\b(offre|utilisez|utilise|réduction|réservation|pour|votre|code|limitée)\b/i;
  const spanishMarkers = /\b(oferta|utiliza|reserva|descuento|tu|código|limitada)\b/i;
  const englishMarkers = /\b(offer|limited|use|booking|discount|your|code|off)\b/i;

  const fr = frenchMarkers.test(lowerText) ? 2 : 0;
  const es = spanishMarkers.test(lowerText) ? 2 : 0;
  const en = englishMarkers.test(lowerText) ? 2 : 0;

  const frenchWords = [' le ', ' la ', ' les ', ' des ', ' une ', ' pour ', ' votre '];
  const spanishWords = [' el ', ' los ', ' las ', ' para ', ' tu ', ' una '];
  const englishWords = [' the ', ' and ', ' for ', ' your ', ' with '];

  const pad = ` ${lowerText} `;
  const count = (words) => words.filter((w) => pad.includes(w)).length;
  const frW = count(frenchWords);
  const esW = count(spanishWords);
  const enW = count(englishWords);

  const scores = {
    fr: fr + frW,
    es: es + esW,
    en: en + enW,
  };
  const best = Object.entries(scores).sort((a, b) => b[1] - a[1])[0];
  if (best[1] > 0) return best[0];
  return 'en';
}

export function sanitizeMessageTranslations(raw, fallbackMessage = '') {
  const base = emptyMessageTranslations();
  if (raw && typeof raw === 'object') {
    Object.entries(raw).forEach(([key, value]) => {
      const code = String(key).toLowerCase();
      if (!code) return;
      const text = String(value ?? '').trim();
      base[code] =
        isBrokenTranslation(text) || isWrongLanguageForCode(text, code) ? '' : text;
    });
  }
  const fb = String(fallbackMessage || '').trim();
  if (fb && !isBrokenTranslation(fb)) {
    const detected = detectSourceLanguage(fb);
    if (detected && !base[detected]) {
      base[detected] = fb;
    }
  }
  return base;
}

export function normalizeMessageTranslations(raw, fallbackMessage = '') {
  return sanitizeMessageTranslations(raw, fallbackMessage);
}

/** Message promo pour la langue du visiteur (fallback en → legacy). */
export function resolvePromoMessage(config, language) {
  if (!config) return '';
  const byLang = sanitizeMessageTranslations(config.message_translations, '');

  const tryLang = (lang) => {
    const t = byLang[lang];
    return t && !isBrokenTranslation(t) ? t : '';
  };

  const preferred = tryLang(language);
  if (preferred) return preferred;

  for (const lang of ['en', language, 'es', 'fr']) {
    const t = tryLang(lang);
    if (t) return t;
  }

  const legacy = String(config.message || '').trim();
  if (legacy && !isBrokenTranslation(legacy)) return legacy;

  return '';
}

/** Langue source = premier onglet rempli valide, ou en. */
export function pickDefaultSourceLang(messageTranslations) {
  const msgs = sanitizeMessageTranslations(messageTranslations, '');
  for (const lang of SITE_LANGUAGE_CODES) {
    if (msgs[lang]) return lang;
  }
  return 'en';
}
